import {
  CreateFieldPayload,
  Field,
  FieldObjectType,
} from 'module/api/field/types';
import { FieldSection } from 'module/api/field-section/types';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { FieldSectionAPI } from 'api/field-sections';
import { FieldAPI } from 'api/fields';
import { useCallback } from 'react';

const ALL_FIELDS_KEY = ['fields'];
const ALL_EXTERNAL_TEAM_MEMBERS_FIELDS_KEY = ['external-team-members-fields'];
const FIELD_SECTIONS_KEY = (fieldSectionType: FieldObjectType) => [
  'field-sections',
  fieldSectionType,
];

export const useGetAllFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<Field[], Error> => {
  return useQuery({
    ...queryOptions,
    queryKey: ALL_FIELDS_KEY,
    queryFn: () =>
      FieldAPI.getAllFields().then((fields) =>
        fields.reduce((acc, field) => acc.concat(field.fields), [] as Field[]),
      ),
  });
};

export const useGetExternalTeamMembersFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<
      Field[],
      Error,
      Field[],
      typeof ALL_EXTERNAL_TEAM_MEMBERS_FIELDS_KEY
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<Field[], Error> => {
  return useQuery({
    ...queryOptions,
    queryKey: ALL_EXTERNAL_TEAM_MEMBERS_FIELDS_KEY,
    queryFn: () =>
      FieldAPI.getAllExternalTeamMembersFields().then(({ fields }) => fields),
  });
};

export const useGetAllCompanyFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey'
  >,
): UseQueryResult<Field[], Error> =>
  useGetAllFieldsQuery({
    ...queryOptions,
    select: (response) =>
      response.filter(
        ({ objectType }) => objectType === FieldObjectType.Company,
      ),
  });

export const useGetAllMeetingFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey'
  >,
): UseQueryResult<Field[], Error> =>
  useGetAllFieldsQuery({
    ...queryOptions,
    select: (response) =>
      response.filter(
        ({ objectType }) => objectType === FieldObjectType.Meeting,
      ),
  });

export const useGetAllEmailFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey'
  >,
): UseQueryResult<Field[], Error> =>
  useGetAllFieldsQuery({
    ...queryOptions,
    select: (response) =>
      response.filter(({ objectType }) => objectType === FieldObjectType.Email),
  });
export const useGetAllSignalFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey'
  >,
): UseQueryResult<Field[], Error> =>
  useGetAllFieldsQuery({
    ...queryOptions,
    select: (response) =>
      response.filter(
        ({ objectType }) => objectType === FieldObjectType.Signal,
      ),
  });
export const useGetAllUserFieldsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<Field[], Error, Field[], typeof ALL_FIELDS_KEY>,
    'queryKey'
  >,
): UseQueryResult<Field[], Error> =>
  useGetAllFieldsQuery({
    ...queryOptions,
    select: (response) =>
      response.filter(({ objectType }) => objectType === FieldObjectType.Users),
  });

export const useGetFieldSectionsQuery = (
  fieldSectionType: FieldObjectType,
  queryOptions?: Omit<
    UseQueryOptions<
      FieldSection[],
      Error,
      FieldSection[],
      ReturnType<typeof FIELD_SECTIONS_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<FieldSection[], Error> => {
  return useQuery({
    ...queryOptions,
    queryKey: FIELD_SECTIONS_KEY(fieldSectionType),
    queryFn: () => FieldSectionAPI.getFieldSections(fieldSectionType),
  });
};
const useInvalidateAllFieldsQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ALL_FIELDS_KEY });
  }, [queryClient]);
};
const useGetSuccessHandlerWithInvalidateAllFieldsQuery = <TData, TVariables>(
  options: UseMutationOptions<TData, Error, TVariables>,
) => {
  const invalidateAllFieldsQuery = useInvalidateAllFieldsQuery();

  return useCallback(
    (data: TData, variables: TVariables, context: unknown) => {
      invalidateAllFieldsQuery();
      options.onSuccess?.(data, variables, context);
    },
    [invalidateAllFieldsQuery, options],
  );
};

export const useCreateFieldMutation = (
  options: UseMutationOptions<Field, Error, CreateFieldPayload> = {},
) => {
  const successHandler =
    useGetSuccessHandlerWithInvalidateAllFieldsQuery(options);

  return useMutation({
    mutationFn: (fieldPayload: CreateFieldPayload) =>
      FieldAPI.createField(fieldPayload),
    onSuccess: successHandler,
    ...options,
  });
};
export const useUpdateFieldMutation = (
  options: UseMutationOptions<
    Field,
    Error,
    { fieldId: number; fieldSectionId: number; payload: CreateFieldPayload }
  > = {},
) => {
  const successHandler =
    useGetSuccessHandlerWithInvalidateAllFieldsQuery(options);

  return useMutation({
    mutationFn: ({ fieldId, payload }) =>
      FieldAPI.updateField(fieldId, payload),
    onSuccess: successHandler,
    ...options,
  });
};
export const useDeleteFieldMutation = (
  options: UseMutationOptions<unknown, Error, Field> = {},
) => {
  const successHandler =
    useGetSuccessHandlerWithInvalidateAllFieldsQuery(options);

  return useMutation({
    mutationFn: (fieldPayload: Field) =>
      FieldAPI.deleteField(fieldPayload.fieldId),
    onSuccess: successHandler,
    ...options,
  });
};
